import { useEffect } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";

import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import { isAuthDomain } from "../../common/utilities/routes/routes.util";
import { AuthDomainRedirect } from "./auth-domain-redirect.comp";
import { LoaderView } from "../loader/loader.comp";

const VerifiedSignInRoute = ({ children }: { children: JSX.Element }) => {
    // Use isAuthenticatedState (reactive) instead of isAuthenticated() (function)
    // This ensures component re-renders when auth state changes
    const { isAuthenticatedState, logout } = FirebaseAuthContainer.useContainer();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // An invite link expresses explicit intent to register/join, and must take
    // precedence over any existing (possibly stale/invalid) session. Without this,
    // an authenticated user landing on /registration?invite=... is redirected to
    // home and the invite is lost; if the session is invalid it then cascades to a
    // failed user-load and a bounce to /login. Instead, sign the stale session out
    // and continue into the registration flow with the invite preserved.
    const hasInvite = !!searchParams.get("invite");

    useEffect(() => {
        if (isAuthenticatedState && hasInvite) {
            logout();
        }
    }, [isAuthenticatedState, hasInvite, logout]);

    // If already authenticated, redirect to home or previous location — unless an
    // invite is being processed, in which case wait for the sign-out above to take
    // effect rather than hijacking the registration to home.
    if (isAuthenticatedState) {
        if (hasInvite) {
            return <LoaderView show={true} />;
        }
        return <Navigate to={location.state?.from || "/"} />;
    }

    // If on auth domain, show the auth forms (children)
    if (isAuthDomain()) {
        return children;
    }

    // If on tenant domain, redirect to auth domain (preserves query params, incl. invite)
    return <AuthDomainRedirect targetPath={location.pathname} currentLocation={location} />;
};

export default VerifiedSignInRoute;
